import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import { FC, useEffect, useRef, useState } from 'react'
import { MdCopyAll, MdDelete, MdEdit, MdKeyboardArrowUp } from 'react-icons/md'

type Props = {
  queues: string[]
  prompt: string
  handleOpenModal: () => void
  handleDeleteQueuePromptPair: () => void
  handleDuplicateQueuePromptPair: () => void
  duplicatedQueues: Set<string>
}

const MAX_SUMMARY_HEIGHT = 200

export const QueueAndPrompts: FC<Props> = ({
  queues,
  prompt,
  handleOpenModal,
  handleDeleteQueuePromptPair,
  handleDuplicateQueuePromptPair,
  duplicatedQueues,
}) => {
  const isDuplicated = queues.some((q) => duplicatedQueues.has(q))

  const [show, setShow] = useState(false)
  const handleToggleShow = (): void => setShow((s) => !s)
  const ref = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(MAX_SUMMARY_HEIGHT)
  const promptRef = useRef<HTMLDivElement>(null)

  const forceShow = MAX_SUMMARY_HEIGHT > (ref.current?.offsetHeight || 0)

  useEffect(() => {
    if (prompt && ref.current) {
      const contentHeight = ref.current.offsetHeight
      setHeight(contentHeight)
    }
  }, [prompt])
  return (
    <Box position="relative" w="100%">
      <Flex
        border="1px"
        borderRadius="md"
        p={2}
        minH="100px"
        flexDirection="column"
        gap={4}
        borderColor={isDuplicated ? 'red.500' : 'greys.100'}
      >
        <Box>
          <Text fontWeight="semibold" color="greys.400" mb={1}>
            <Trans>Queues</Trans>
          </Text>
          <Wrap spacing={2}>
            {queues.map((queue, index) => {
              const isDuplicated = duplicatedQueues.has(queue)
              return (
                <Tooltip
                  isDisabled={!isDuplicated}
                  key={index}
                  hasArrow
                  placement="top"
                  label={t`This queue is duplicated. Remove it or its duplicate(s) to be able to save.`}
                >
                  <WrapItem>
                    <Tag
                      size="md"
                      borderRadius="full"
                      colorScheme={isDuplicated ? 'red' : 'greys'}
                    >
                      <TagLabel>{queue}</TagLabel>
                    </Tag>
                  </WrapItem>
                </Tooltip>
              )
            })}
          </Wrap>
        </Box>
        <Box>
          <Text size="md" fontWeight="semibold">
            <Trans>Prompt</Trans>
          </Text>
          <HStack
            pt={2}
            w="100%"
            justifyContent="space-between"
            alignItems="start"
            cursor={forceShow ? undefined : 'pointer'}
            onClick={handleToggleShow}
            _hover={{
              background: forceShow ? undefined : 'gray.200',
            }}
          >
            <Box gap={2} textAlign="start" w="100%">
              <Box
                transition="height 200ms"
                height={forceShow || show ? height : MAX_SUMMARY_HEIGHT}
                mb="4"
                w="100%"
                position="relative"
                overflow="hidden"
              >
                <Box ref={ref}>
                  <Box ml={10} ref={promptRef}>
                    <Text whiteSpace="pre-wrap">{prompt}</Text>
                  </Box>
                </Box>
              </Box>
              {!forceShow && (
                <Box
                  transition="height 300ms"
                  pos={show ? 'static' : 'absolute'}
                  bottom="0px"
                  left="0"
                  right="0"
                  w="100%"
                  h={show ? '12px' : '50px'}
                  background={
                    show
                      ? 'none'
                      : 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, white 100%)'
                  }
                >
                  <Icon
                    transform={show ? 'scale(1, 1)' : 'scale(1, -1)'}
                    pos="absolute"
                    left="50%"
                    ml="-20px"
                    bottom="4px"
                    as={MdKeyboardArrowUp}
                    fontSize="20px"
                  />
                </Box>
              )}
            </Box>
          </HStack>
        </Box>
        <HStack position="absolute" top={2} right={2} spacing={1}>
          <IconButton
            aria-label="Copy paste prompt and queue block"
            icon={<MdCopyAll />}
            size="sm"
            variant="ghost"
            onClick={handleDuplicateQueuePromptPair}
          />
          <IconButton
            aria-label="Delete queues"
            icon={<MdDelete />}
            size="sm"
            variant="ghost"
            onClick={handleDeleteQueuePromptPair}
          />
          <IconButton
            aria-label="Edit queues"
            icon={<MdEdit />}
            size="sm"
            variant="ghost"
            onClick={handleOpenModal}
          />
        </HStack>
      </Flex>
    </Box>
  )
}
